import { Button, Image, message, Spin } from "antd";
import MarkupLayerDbPdf from "libs/pdfLib/components/markupsDbLayer";
import PdfLoader from "libs/pdfLib/components/PdfLoader";
import { AttachmentDoc } from "models/submittal-log";
import { useContext, useEffect, useState } from "react";
import { getFileDownloadURL } from "services/file-mgmt";
import { downloadFileInBrowser } from "utils/utils";
import useAuditEventLog from "entity-app/utils/export-audit-log";
import { ProjectContext, TProjectContext } from "context/ProjectProvider";

// const imagesFileTypes = ["jpg", "jpeg", "png", "jfif"];
// const pdfFileTypes = ["pdf"];
// const imageReg = new RegExp(`.(${imagesFileTypes.join("|")})$`, "i");
// const pdfReg = new RegExp(`.(${pdfFileTypes.join("|")})$`, "i");

function DocumentView({
  file,
  searchTexts,
  pdfStyle,
  auditFeatureType = null
}: {
  file: AttachmentDoc;
  searchTexts?: string[] | undefined;
  pdfStyle?: React.CSSProperties;
  /** featureType for audit log (e.g. SUBMITTALS, MATERIALS). */
  auditFeatureType?: string | null;
}) {
  const [url, setUrl] = useState<string | undefined>(undefined);
  const [error, setError] = useState(false);
  const { gqlClientForProject }: TProjectContext = useContext(ProjectContext);
  const { saveAuditLogEvent } = useAuditEventLog(gqlClientForProject);

  async function init(f: AttachmentDoc, logView = true) {
    const fileDownloadURLResp: any = await getFileDownloadURL([f.blob_key]);
    if (fileDownloadURLResp.data) {
      const link = fileDownloadURLResp.data.success.urls[f.blob_key];
      setUrl(link);
      if (logView) {
        saveAuditLogEvent({
          eventType: "FILE_ATTACHMENT_VIEW",
          featureType: auditFeatureType,
          filename: f.file_name || "",
          ...(f.id ? { attachmentId: f.id } : {})
        });
      }
    }
    if (fileDownloadURLResp.error) {
      setError(true);
      message.error(fileDownloadURLResp.error.msg);
    }
  }

  useEffect(() => {
    setUrl(undefined);
    setError(false);
    init(file);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-fetch only when the document changes
  }, [file]);

  const onExpiredUrl = () => {
    setUrl(undefined);
    setError(false);
    init(file, false);
  };

  function DocumentViewer() {
    if (error)
      return (
        <div className="flex justify-center items-center h-full w-full">
          <div>Something wrong</div>
        </div>
      );
    if (!url)
      return (
        <div className="flex justify-center items-center h-full w-full">
          <Spin size="large" />
        </div>
      );

    // Check if it's a PSD or SVG file
    const isPsdORSvg =
      file.file_name.toLowerCase().endsWith(".psd") ||
      file.file_type.toLowerCase().indexOf("photoshop") >= 0 ||
      file.file_type.toLowerCase().indexOf("svg") >= 0;

    if (!isPsdORSvg && file.file_type.toLowerCase().indexOf("image") >= 0) {
      return <Image className="h-full w-full" src={url} />;
    }
    if (file.file_type.toLowerCase().indexOf("pdf") >= 0) {
      return (
        <PdfLoader url={url} onExpiredUrl={onExpiredUrl}>
          {(pdfDocument) => (
            <MarkupLayerDbPdf
              file={file}
              pdfDocument={pdfDocument}
              searchTexts={searchTexts}
              pdfStyle={pdfStyle}
            />
          )}
        </PdfLoader>
      );
    }
    return (
      <div>
        This file can&lsquo;t be viewed, click
        <Button
          type="link"
          className="italic hover:border-none"
          onClick={() => {
            downloadFileInBrowser(url, file.file_name);
            saveAuditLogEvent({
              eventType: "FILE_ATTACHMENT_DOWNLOAD",
              featureType: auditFeatureType,
              filename: file.file_name || "",
              ...(file.id ? { attachmentId: file.id } : {})
            });
          }}
          data-testid="index-button-downloadfile"
        >
          here
        </Button>
        to download the file.
      </div>
    );
  }

  return <DocumentViewer />;
}

export default DocumentView;
